import React from 'react';
import { 
  ArrowRight, 
  Zap, 
  Shield,
  BookmarkPlus,
  Bell,
  Unlock,
} from 'lucide-react';

/**
 * ShareSignUpCTA - Streamlined call-to-action for share page recipients
 * 
 * Single focus: Sign up to unlock and auto-save the shared results.
 * List is automatically saved to account after authentication.
 */
export default function ShareSignUpCTA({
  resultsCount = 0,
  title = 'This Research',
  onSignUp,
  isAuthenticated = false,
  variant = 'banner', // 'banner' | 'inline' | 'sticky' | 'modal'
  className = '',
}) {
  // Value propositions
  const features = [
    { icon: BookmarkPlus, text: 'Auto-saved to your account' },
    { icon: Zap, text: 'Full contact details unlocked' },
    { icon: Bell, text: 'Notifications for new matches' },
  ];

  // Banner variant - prominent horizontal CTA
  if (variant === 'banner') {
    return (
      <div className={`relative overflow-hidden ${className}`}>
        {/* Gradient background */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#5a20ff] via-[#6366f1] to-[#4f46e5]" />
        
        {/* Subtle pattern overlay */}
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
        
        <div className="relative px-6 py-5">
          <div className="flex items-center justify-between gap-6 flex-wrap">
            {/* Left: Message */}
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
                <Unlock className="w-6 h-6 text-white" />
              </div>
              <div className="text-white">
                <p className="text-sm font-medium text-white/80">
                  {resultsCount} contacts ready to unlock
                </p>
                <h3 className="text-lg font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Sign in to access full details
                </h3>
              </div>
            </div>

            {/* Right: CTA */}
            <button
              onClick={onSignUp}
              className="
                flex items-center gap-2 px-6 py-3 
                bg-white text-[#5a20ff] rounded-xl font-bold text-sm
                shadow-lg shadow-black/10
                hover:shadow-xl hover:-translate-y-0.5
                transition-all duration-200
              "
            >
              Sign In to Unlock
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Sticky variant - fixed bottom bar
  if (variant === 'sticky') {
    return (
      <div className={`fixed bottom-0 left-0 right-0 z-50 ${className}`}>
        <div className="bg-gradient-to-r from-[#5a20ff] to-[#6366f1] shadow-2xl border-t border-white/10">
          <div className="max-w-5xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 text-white">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                  <Unlock className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold">Unlock {resultsCount} contacts</p>
                  <p className="text-xs text-white/70">Auto-saved to your account</p>
                </div>
              </div>
              
              <button
                onClick={onSignUp}
                className="flex items-center gap-2 px-5 py-2.5 bg-white text-[#5a20ff] rounded-xl font-bold text-sm hover:shadow-lg transition-all"
              >
                Sign In <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Inline variant - card-style CTA
  if (variant === 'inline') {
    return (
      <div className={`bg-gradient-to-br from-[#5a20ff]/5 via-white to-[#52dcb3]/5 rounded-2xl border border-[#5a20ff]/10 p-6 ${className}`}>
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#5a20ff] to-[#52dcb3] flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#5a20ff]/20">
            <Unlock className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-black mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
              Unlock full access
            </h3>
            <p className="text-sm text-black/60 mb-4">
              Sign in to unlock all {resultsCount} contacts with full details.
            </p>
            
            {/* Features */}
            <div className="flex flex-wrap gap-3 mb-5">
              {features.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-2 text-xs text-black/70">
                  <feature.icon className="w-3.5 h-3.5 text-[#5a20ff]" />
                  <span>{feature.text}</span>
                </div>
              ))}
            </div>
            
            {/* CTA */}
            <button
              onClick={onSignUp}
              className="
                flex items-center gap-2 px-5 py-2.5 
                bg-gradient-to-r from-[#5a20ff] to-[#6366f1] text-white 
                rounded-xl font-bold text-sm
                shadow-lg shadow-[#5a20ff]/20
                hover:shadow-xl hover:-translate-y-0.5
                transition-all duration-200
              "
            >
              Sign In to Unlock
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Modal variant - full overlay
  if (variant === 'modal') {
    return (
      <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${className}`}>
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
        
        <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="relative bg-gradient-to-br from-[#5a20ff] via-[#6366f1] to-[#4f46e5] p-8 text-center text-white">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center mb-4">
              <Unlock className="w-8 h-8" />
            </div>
            <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              Unlock Full Access
            </h2>
            <p className="text-white/80">
              {resultsCount} contacts ready to unlock
            </p>
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="space-y-3 mb-6">
              {[
                { icon: BookmarkPlus, text: 'Auto-saved to your account' },
                { icon: Shield, text: 'Full contact details' },
                { icon: Bell, text: 'Alerts for new matches' },
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-black/[0.02] rounded-xl">
                  <div className="w-8 h-8 rounded-lg bg-[#5a20ff]/10 flex items-center justify-center">
                    <item.icon className="w-4 h-4 text-[#5a20ff]" />
                  </div>
                  <span className="text-sm text-black/80">{item.text}</span>
                </div>
              ))}
            </div>

            <button
              onClick={onSignUp}
              className="
                w-full flex items-center justify-center gap-2 px-6 py-4
                bg-gradient-to-r from-[#5a20ff] to-[#52dcb3] text-white
                rounded-xl font-bold text-base
                shadow-lg shadow-[#5a20ff]/20
                hover:shadow-xl hover:-translate-y-0.5
                transition-all duration-200
              "
            >
              Sign In to Unlock
              <ArrowRight className="w-5 h-5" />
            </button>

            <p className="text-center text-xs text-black/40 mt-4">
              Free to sign up
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
